
import { ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { useNavigate } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";

interface IndustryPageLayoutProps {
  industry: {
    title: string;
    icon: React.ComponentType<any>;
    gradient: string;
    description: string;
    stats: string;
  };
  heroContent: React.ReactNode;
  mainContent: React.ReactNode;
  imageGallery?: React.ReactNode;
}

export const IndustryPageLayout = ({
  industry,
  heroContent,
  mainContent,
  imageGallery
}: IndustryPageLayoutProps) => {
  const navigate = useNavigate();
  const Icon = industry.icon;

  const handleStartCreating = () => {
    navigate('/auth');
  };

  const handleStartFreeTrial = () => {
    navigate('/auth');
  };

  const handleViewExamples = () => {
    // Scroll to image gallery section if it exists, otherwise scroll to top
    if (imageGallery) {
      const imageGalleryElement = document.querySelector('[data-section="image-gallery"]');
      if (imageGalleryElement) {
        imageGalleryElement.scrollIntoView({ behavior: 'smooth' });
      }
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  return (
    <div className="min-h-screen w-full overflow-x-hidden bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      <Header />
      
      {/* Hero Section */}
      <section className="py-12 sm:py-16 relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-[linear-gradient(to_right,#80808012_1px,transparent_1px),linear-gradient(to_bottom,#80808012_1px,transparent_1px)] bg-[size:24px_24px]"></div>
          <div className="absolute inset-0 bg-gradient-to-br from-blue-600/10 via-transparent to-purple-600/10"></div>
        </div>
        
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <Button onClick={() => navigate('/')} variant="outline" className="mb-8 bg-white/10 border-white/20 text-white hover:bg-white/20">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Home
          </Button>
          
          <div className="max-w-4xl mx-auto text-center">
            <div className={`w-16 h-16 sm:w-20 sm:h-20 bg-gradient-to-r ${industry.gradient} rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-2xl`}>
              <Icon className="w-8 h-8 sm:w-10 sm:h-10 text-white" />
            </div>
            
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-4 leading-tight">
              AI for <span className={`bg-gradient-to-r ${industry.gradient} bg-clip-text text-transparent`}>{industry.title}</span>
            </h1>
            
            <p className="text-base sm:text-lg text-gray-300 mb-6 leading-relaxed max-w-3xl mx-auto">
              {industry.description}
            </p>
            
            <div className="flex flex-col sm:flex-row gap-3 justify-center items-center">
              <div className={`px-4 py-2 bg-gradient-to-r ${industry.gradient} rounded-full`}>
                <span className="text-white font-semibold text-sm">{industry.stats}</span>
              </div>
              <Button 
                onClick={handleStartCreating}
                className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white px-8 py-3 rounded-lg font-medium"
              >
                Start Creating
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Hero Content */}
      <section className="py-8 relative">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          {heroContent}
        </div>
      </section>

      {/* Main Article Content */}
      <section className="py-12 relative">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            {mainContent}
          </div>
        </div>
      </section>

      {/* Image Gallery */}
      {imageGallery && (
        <section className="py-12 relative" data-section="image-gallery">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            {imageGallery}
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className="py-12 relative">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <Card className="bg-white/5 backdrop-blur-md border-white/10 max-w-4xl mx-auto">
            <CardContent className="p-6 sm:p-8 text-center">
              <h3 className="text-xl sm:text-2xl font-bold text-white mb-3">
                Ready to Transform Your {industry.title} Business?
              </h3>
              <p className="text-gray-300 mb-4 text-base">
                Join thousands of {industry.title.toLowerCase()} professionals already using AI to create stunning visuals.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button 
                  onClick={handleStartFreeTrial}
                  className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white px-8 py-3 rounded-lg font-medium"
                >
                  Start Free Trial
                </Button>
                <Button 
                  onClick={handleViewExamples}
                  variant="outline" 
                  className="border-white/20 text-white px-8 py-3 rounded-lg font-medium bg-white/0 hover:bg-white/10"
                >
                  View Examples
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      <Footer />
    </div>
  );
};
